import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, effect, inject, model, output, signal, ViewChild } from '@angular/core';
import { INotificationResponse } from 'app/core/interfaces/notification.interface';
import { AlertService } from 'app/shared/services/alert/alert.service';
import { NotificationService } from 'app/core/services/notifications/notification.service';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { SkeletonModule } from 'primeng/skeleton';
import { finalize } from 'rxjs';
import { InfiniteScrollDirective } from 'app/shared/directives/infinite-scroll.directive';
import { Router, RouterModule } from "@angular/router";
import { TabsModule } from 'primeng/tabs';

export interface INewsItem {
  id: number;
  date: string;
  title: string;
  body: string;
  ctaLabel?: string;
  unread: boolean;
}

@Component({
  selector: 'notification-modal',
  standalone: true,
  imports: [CommonModule, PopoverModule, ButtonModule, DatePipe, InfiniteScrollDirective, RouterModule, SkeletonModule, TabsModule],
  templateUrl: './notification-modal.component.html',
  styleUrl: './notification-modal.component.css'
})
export class NotificationModalComponent {
  @ViewChild('op') op!: Popover;
  visible = model<boolean>(false);
  closed = output<void>();

  private readonly notificationService = inject(NotificationService);
  private readonly alertService = inject(AlertService);
  private readonly router = inject(Router);

  readonly loading = signal<boolean>(false);
  private anchorEvent = signal<MouseEvent | null>(null);
  isLoading = signal<boolean>(false);
  notifications = signal<INotificationResponse[]>([]);

  readonly totalRecords = signal<number>(0);
  readonly page = signal<number>(1);
  rows = 5;

  readonly hasMore = computed(() =>
    this.notifications().length < this.totalRecords()
  );

  readonly skeletonRows = signal(Array.from({ length: this.rows }));

  readonly initialLoading = computed(
    () => this.loading() && this.notifications().length === 0,
  );
  readonly loadingMore = computed(
    () => this.loading() && this.notifications().length > 0,
  );

  readonly selectedTab = signal<string>('0');
  setSelectedTab(value: string | number): void {
    this.selectedTab.set(String(value));
  }

  readonly news = signal<INewsItem[]>([
    {
      id: 1,
      date: '2025-06-02T12:40:00',
      title: 'Nuevos forma de cargar archivos',
      body: 'Ahora disponible desde la base de conocimiento.',
      ctaLabel: 'Probar ahora.',
      unread: true,
    },
    {
      id: 2,
      date: '2025-06-02T12:40:00',
      title: 'Conecta tu correo electrónico a Vault.',
      body: 'ahora podrás conectar y recibir tus correos en un solo lugar.',
      ctaLabel: 'Descubrir ahora.',
      unread: true,
    },
    {
      id: 3,
      date: '2025-06-02T12:40:00',
      title: 'Mejoramos tu experiencia.',
      body: 'La carga de documentos ahora será más rápida.',
      unread: false,
    },
    {
      id: 4,
      date: '2025-06-02T12:40:00',
      title: 'Métodos de reportes actualizado.',
      body: 'Ahora puedes generar tus propios reportes.',
      ctaLabel: 'Probar ahora.',
      unread: false,
    },
    {
      id: 5,
      date: '2025-06-02T12:40:00',
      title: '¡Nuevos filtros dentro de Vault!',
      body: 'Ahora puedes filtrar por estado del caso.',
      unread: false,
    },
  ]);

  readonly APPROVAL_ASSOCIATION = 1;
  readonly JOINED_WORKSPACE = 2;
  readonly APPROVAL_DOCUMENT = 7;
  readonly STALE_DOCUMENT = 8;
  readonly REPLACE_DOCUMENT = 3;

  loadMore() {
    if (this.loading() || !this.hasMore()) return;
    this.page.update(p => p + 1);
    this.fetchNotification(true);
  }

  constructor() {
    effect(() => {
      const isOpen = this.visible();
      const ev = this.anchorEvent();

      if (!this.op) return;

      if (isOpen) {
        if (ev) this.op.show(ev);
      } else {
        this.op.hide();
      }
    });
  }

  openFromClick(ev: MouseEvent) {
    this.anchorEvent.set(ev);
    this.visible.set(true);

    this.page.set(1);
    this.notifications.set([]);
    this.totalRecords.set(0);

    this.fetchNotification(false);
  }

  onPopoverHide() {
    this.visible.set(false);
    this.closed.emit();
  }

  goToPanelNotification(): void {
    this.router.navigate(['veflo-vault/notification/requests-panel']);
    this.visible.set(false);
  }

  fetchNotification(append = false) {
    if (this.loading()) return;
    this.loading.set(true);

    this.notificationService.getNotifications(this.page(), this.rows).pipe(
      finalize(() => this.loading.set(false))
    )
    .subscribe({
      next: ({ items, total }) => {
        if (append) {
          const merged = [...this.notifications(), ...(items ?? [])];
          const dedup = Array.from(new Map(merged.map(i => [i.notificationID, i])).values());
          this.notifications.set(dedup);
        } else {
          this.notifications.set(items ?? []);
        }
        this.totalRecords.set(total ?? 0);
      },
      error: () => {
        this.alertService.showAlertError(
          'Error al cargar las notificaciones',
          'No pudimos cargar las notificaciones. Intenta nuevamente.'
        );
      }
    });
  }

  postNotificationDecision(notificationID: string, status: boolean) {
    const item = {
      notificationID,
      status,
    };

    this.isLoading.set(true);

    this.notificationService.postWorkspace(item).pipe(
      finalize(() => {
        this.isLoading.set(false);
      })
    )
    .subscribe({
      next: () => {
        this.fetchNotification();
      },
      error: (err) => {
        this.alertService.showAlertError(
          'Error al actualizar la notificación.',
          err.error?.message || 'Ocurrió un problema con el servidor, inténtalo más tarde'
        );
      },
    });
  }
}